/*
 * Copyright (C) 2026 Nex4Nova (Geoblast group)
 * SPDX-License-Identifier: Proprietary
 */
import * as THREE from 'three';

const CAMERA_CACHE_KEY = 'nex4nova-drilling-camera-v2';

export function enforceZUpOrbit(scene) {
  const camera = scene?.camera;
  const controls = scene?.controls;
  if (!camera || !controls) return false;
  const target = controls.target || new THREE.Vector3(0, 0, 0);
  const offset = camera.position.clone().sub(target);
  const distance = offset.length();
  if (distance > 0) {
    const zAlignment = Math.abs(offset.normalize().dot(new THREE.Vector3(0, 0, 1)));
    if (zAlignment > 0.999) {
      camera.position.x += Math.max(distance * 0.001, 1);
    }
  }
  camera.up.set(0, 0, 1);
  camera.lookAt(target);
  controls.update();
  return true;
}

export function getSceneViewState(scene) {
  if (!scene) return null;
  if (typeof scene.getViewState === 'function') return scene.getViewState();
  const camera = scene.camera;
  const target = scene.controls?.target;
  if (!camera || !target) return null;
  return {
    camera: { x: camera.position.x, y: camera.position.y, z: camera.position.z },
    target: { x: target.x, y: target.y, z: target.z },
    up: { x: camera.up.x, y: camera.up.y, z: camera.up.z },
  };
}

export function setSceneViewState(scene, viewState) {
  if (!scene || !viewState) return false;
  if (typeof scene.setViewState === 'function') {
    const applied = scene.setViewState(viewState);
    if (applied) enforceZUpOrbit(scene);
    return applied;
  }
  const camera = scene.camera;
  const controls = scene.controls;
  if (!camera || !controls) return false;
  const cam = viewState.camera || {}, tgt = viewState.target || {};
  const values = [cam.x, cam.y, cam.z, tgt.x, tgt.y, tgt.z];
  if (!values.every(Number.isFinite)) return false;
  camera.position.set(cam.x, cam.y, cam.z);
  controls.target.set(tgt.x, tgt.y, tgt.z);
  camera.lookAt(tgt.x, tgt.y, tgt.z);
  enforceZUpOrbit(scene);
  return true;
}

export function recenterCameraToOriginZUp(scene, distance = 1000) {
  if (!scene?.camera || !scene?.controls) return;
  const target = new THREE.Vector3(0, 0, 0);
  scene.controls.target.copy(target);
  scene.camera.position.set(target.x + distance, target.y + distance, target.z + distance);
  enforceZUpOrbit(scene);
}

export function focusOnLastBoundsZUp(scene, padding = 1.2) {
  if (!scene?.lastBounds || !scene?.camera || !scene?.controls) return;
  scene.focusOnLastBounds?.(padding);
  enforceZUpOrbit(scene);
}

export function lookDownZUp(scene, distance = 2000) {
  if (!scene?.camera || !scene?.controls) return;
  const target = scene.lastBounds ? centerFromBounds(scene.lastBounds) : new THREE.Vector3(0, 0, 0);
  const safeDistance = Number.isFinite(distance) && distance > 0 ? distance : 2000;
  const nudge = Math.max(safeDistance * 0.001, 1);
  scene.controls.target.copy(target);
  scene.camera.position.set(target.x + nudge, target.y, target.z + safeDistance);
  enforceZUpOrbit(scene);
}

export function centerFromBounds(bounds) {
  return new THREE.Vector3(
    (Number(bounds.minX) + Number(bounds.maxX)) / 2,
    (Number(bounds.minY) + Number(bounds.maxY)) / 2,
    (Number(bounds.minZ) + Number(bounds.maxZ)) / 2
  );
}

export function boundsFromBox(box) {
  return {
    minX: box.min.x,
    maxX: box.max.x,
    minY: box.min.y,
    maxY: box.max.y,
    minZ: box.min.z,
    maxZ: box.max.z,
  };
}

export function reconcileSceneBounds(scene, holes, objGroups) {
  const box = new THREE.Box3();
  let hasBounds = false;
  (holes || []).forEach((hole) => {
    (hole.points || []).forEach((point) => {
      const x = Number(point?.x), y = Number(point?.y), z = Number(point?.z);
      if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return;
      box.expandByPoint(new THREE.Vector3(x, y, z));
      hasBounds = true;
    });
  });
  objGroups.forEach((group) => {
    const objBox = new THREE.Box3().setFromObject(group);
    if (objBox.isEmpty()) return;
    box.union(objBox);
    hasBounds = true;
  });
  scene.lastBounds = hasBounds ? boundsFromBox(box) : null;
}

export function loadCachedCameraView() {
  try {
    const raw = localStorage.getItem(CAMERA_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (e) { return null; }
}

export function saveCachedCameraView(viewState) {
  try { localStorage.setItem(CAMERA_CACHE_KEY, JSON.stringify(viewState)); } catch (e) { /* ignore */ }
}

export function disposeMaterial(material) {
  if (Array.isArray(material)) {
    material.forEach((entry) => entry?.dispose?.());
    return;
  }
  material?.dispose?.();
}

export function disposeObject3D(object) {
  object.traverse((child) => {
    if (!child.isMesh) return;
    child.geometry?.dispose?.();
    disposeMaterial(child.material);
  });
}
